import type { Account, ExternalProductAccount } from '@shared/products-types';
import {
    AccountSubtype,
    AccountType,
    CardType,
    ExternalProductType,
    FLAG,
    MultibankingAccountType,
} from '@shared/products-types';
import type { Insurance } from '@retail/insurance-types';
import type { Pension } from '@retail/pension-types';
import type { Leasing } from '@retail/leasing-types';
import { Characteristic } from '@shared/cards-types';
import type { SecurityResource } from '@retail/investments-types';
import type { ProductWithHelpers } from '@shared/products-core';
import { isCurrentAccount, isProductType } from '@shared/products-core';

import type { BuildingSociety } from '../types';

export const getProductSubtype = (product: ProductWithHelpers): AccountSubtype | undefined => {
    // @ts-expect-error - invalid typing: API can sometimes return subType instead of subtype, type should be updated with clear reasoning
    return product.subtype ?? product.subType;
};

export const isExtBankCurrentAccount = (account: ProductWithHelpers): boolean =>
    isProductType(account, MultibankingAccountType['EXT-BANK-CURRENT']);

export const isExtBank = (account: ProductWithHelpers): boolean =>
    isProductType(account, ExternalProductType.EXTBANK);

export const isDigiVVSettlementAccount = (account: ProductWithHelpers): boolean =>
    isCurrentAccount(account) &&
    getProductSubtype(account) === AccountSubtype.AT_SECURITIES_DIGIVV_SETTLEMENT;

export const isSFondsPlanAccount = (account: ProductWithHelpers): boolean =>
    isCurrentAccount(account) &&
    getProductSubtype(account) === AccountSubtype.AT_SECURITIES_FOND_SETTLEMENT;

export const isSavingsAccount = (account: ProductWithHelpers): boolean =>
    isProductType(account, AccountType.SAVING);

export const isLoanAccount = (account: ProductWithHelpers): boolean =>
    isProductType(account, AccountType.LOAN);

export const isBuildingSociety = (
    account: ProductWithHelpers,
): account is ProductWithHelpers<ExternalProductAccount, BuildingSociety> =>
    isProductType(account, ExternalProductType.BUILDINGSOCIETY);

export const isBankCard = (account: ProductWithHelpers): boolean =>
    isProductType(account, CardType.BANK_CARD) || isProductType(account, CardType.VIRTUAL_DEBIT);

export const isCreditCard = (account: ProductWithHelpers): boolean =>
    isProductType(account, CardType.CREDIT) || isProductType(account, CardType.VIRTUAL_CREDIT);

export const isVirtualAuthorizedCreditCard = (account: ProductWithHelpers<Account>): boolean =>
    isProductType(account, CardType.VIRTUAL_CREDIT) &&
    account.card?.characteristic === Characteristic.AUTHORIZED;

export const isMainCreditCard = (account: ProductWithHelpers<Account>): boolean =>
    isProductType(account, CardType.CREDIT) && account.card?.characteristic === Characteristic.MAIN;

export const isPrepaidCard = (account: ProductWithHelpers): boolean =>
    isProductType(account, CardType.PREPAID);

export const isIndoorCard = (account: ProductWithHelpers): boolean =>
    isProductType(account, CardType.INDOOR);

export const isSecurityAccount = (
    account: ProductWithHelpers,
): account is ProductWithHelpers<ExternalProductAccount, SecurityResource> =>
    isProductType(account, ExternalProductType.SECURITY);

export const isTermDeposit = (account: ProductWithHelpers): boolean =>
    isSavingsAccount(account) && getProductSubtype(account) === AccountSubtype.TERM_DEPOSIT;

export const isInsurance = (
    account: ProductWithHelpers,
): account is ProductWithHelpers<ExternalProductAccount, Insurance> =>
    isProductType(account, ExternalProductType.INSURANCE);

export const isLeasing = (
    account: ProductWithHelpers,
): account is ProductWithHelpers<ExternalProductAccount, Leasing> =>
    isProductType(account, ExternalProductType.LEASING);

export const isPension = (
    account: ProductWithHelpers,
): account is ProductWithHelpers<ExternalProductAccount, Pension> =>
    isProductType(account, ExternalProductType.PENSION);

export const isCard = (account: ProductWithHelpers): boolean =>
    isBankCard(account) || isCreditCard(account) || isPrepaidCard(account) || isIndoorCard(account);
/** Helpers for subtypes */

export const isCreditCardAccount = (account: ProductWithHelpers): boolean =>
    isProductType(account, AccountType.CREDIT_CARD) &&
    getProductSubtype(account) === AccountSubtype.CREDIT_CARD_ACCOUNT;

export const isDigiVVManagementAccount = (account: ProductWithHelpers): boolean => {
    if (!account || !isSecurityAccount(account as Account)) {
        return false;
    }

    const flags = (account.underlyingData as SecurityResource)?.flags ?? [];
    return flags.includes(FLAG.trueWealthAccount);
};
export const isMoneyOnClickAccount = (account: Account): boolean =>
    isProductType(account, AccountType.LOAN) &&
    getProductSubtype(account) === AccountSubtype.REVOLVING_LOAN;
export const isLegacyHRKAccount = (account: ProductWithHelpers): boolean =>
    Boolean((account as Account).flags?.includes('hr-legacyCurrencyAccount'));
